import React, { createContext, useContext, useState, useEffect } from "react";
import authService from "../services/authService";
import { getErrorMessage } from "../utils/errorHandler";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if token exists on load and fetch user info
    const checkAuth = async () => {
      const token = localStorage.getItem("smarthr_token");
      if (token) {
        try {
          const profile = await authService.getMe();
          // Extract user attributes and map user profile information
          const formattedUser = {
            id: profile.id,
            email: profile.email,
            role: profile.role.toLowerCase(), // frontend expects lowercase (rh, manager, employee)
            department: profile.employee ? profile.employee.department : "Non spécifié",
            name: profile.employee 
              ? `${profile.employee.first_name} ${profile.employee.last_name}`
              : "Utilisateur",
            employeeId: profile.employee ? profile.employee.id : null,
            avatar: null // Will use default avatar in components
          };
          setUser(formattedUser);
          localStorage.setItem("smarthr_user", JSON.stringify(formattedUser));
        } catch (error) {
          console.error("Token verification failed:", error);
          authService.logout();
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const loginResponse = await authService.login(email, password);
      
      // Use user info from login response
      const formattedUser = {
        id: loginResponse.user_id,
        email: loginResponse.email,
        role: loginResponse.role.toLowerCase(),
        department: "Non spécifié",
        name: "Utilisateur",
        employeeId: null,
        avatar: null // Will use default avatar in components
      };
      
      // Try to get full profile for additional info
      try {
        const profile = await authService.getMe();
        if (profile.employee) {
          formattedUser.department = profile.employee.department || "Non spécifié";
          formattedUser.name = `${profile.employee.first_name} ${profile.employee.last_name}`;
          formattedUser.employeeId = profile.employee.id;
        }
      } catch (profileError) {
        console.warn("Could not fetch full profile, using basic info:", profileError);
      }
      
      setUser(formattedUser);
      localStorage.setItem("smarthr_user", JSON.stringify(formattedUser));
      setLoading(false);
      return { success: true, role: formattedUser.role };
    } catch (error) {
      console.error("Login failed:", error);
      setLoading(false);
      const errorMsg = getErrorMessage(error);
      return { success: false, error: errorMsg };
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
